/**
 * Tipos do formulário de criação/edição de contrato.
 * Usados para substituir `any[]` em linhas e tipar o estado do formulário.
 */

/** Oferta de uma linha (formulário) */
export interface ContractOfferFormItem {
  id?: string;
  nomeOferta: string;
  valorMensal: number;
  incluso: boolean;
}

/** Linha do contrato no formulário (estado `linhas`) */
export interface ContractLineFormItem {
  id?: string;
  negociacao: string;
  ddd: string;
  numero: string;
  chipCcid: string;
  tipoVenda: string;
  planId: string;
  planName?: string;
  plano?: string;
  aparelho: string;
  handsetDeviceId?: string;
  aparelhoValorTotal?: number;
  aparelhoValorMensal?: number;
  valorChip: number | string;
  valorMensal: number;
  valorTotalMensal?: number;
  quantidade: number;
  ofertas: ContractOfferFormItem[];
  excluirDoContrato: boolean;
  requerSuspensao: boolean;
  trocaLinha: boolean;
  meuTimLogin: string;
  meuTimSenha: string;
  protocolo: string;
  seraUsadaPeloCliente: boolean | null;
  ehPortabilidade: boolean;
  portabilidadeNomeCompleto: string;
  portabilidadeCpf: string;
  portabilidadeOperadora: string;
  portabilidadeLinha: string;
  /** CNPJ do cedente, quando a portabilidade é entre CNPJs diferentes. */
  portabilidadeCnpjCedente?: string;
  /** Razão social do cedente, exigida junto com o CNPJ do cedente. */
  portabilidadeRazaoSocialCedente?: string;
  /** Representantes legais do cedente (nome + CPF). */
  portabilidadeRepresentantes?: { nome: string; cpf: string }[];
  /** e-SIM: chip virtual — ativado via QR Code após aprovação CQV */
  isEsim?: boolean;
  /** Contrato alvo após roteamento por negociação (PATCH vai para este id). */
  sourceContractId?: string;
  /** Contrato onde a linha estava ao carregar o formulário (não muda ao trocar negociação). */
  lineOriginContractId?: string;
  /** Marcar para desativar (aba Linhas a Desativar) */
  marcarParaDesativar?: boolean;
  dataDesativacao?: string;
}

/** Representante no formulário (QSA ou manual) */
export interface ContractRepresentanteFormItem {
  id?: string;
  nome: string;
  qualificacao?: string;
  cpf: string;
  rg?: string;
  email?: string;
  telefone?: string;
  /** true = adicionado manualmente; false = veio do QSA/Partner */
  manual?: boolean;
  /** CPF mascarado da Receita/CNPJ WS (ex.: ***886437**) — só para orientação na UI. */
  cpfHintFromQsa?: string;
}

/** Recebedor autorizado no formulário */
export interface ContractRecebedorFormItem {
  id?: string;
  nome: string;
  qualificacao?: string;
  cpf: string;
  rg?: string;
  email?: string;
  telefone?: string;
  celular?: string;
  manual?: boolean;
}

/** Testemunha no formulário */
export interface ContractTestemunhaFormItem {
  id: string;
  nome: string;
  cpf: string;
  rg: string;
}

/** Endereço de entrega no formulário */
export interface ContractAddressFormState {
  cep: string;
  cidade: string;
  estado: string;
  endereco: string;
  numero: string;
  bairro: string;
  complemento: string;
  referencia: string;
}

/** Responsável pela auditoria no formulário */
export interface ContractAdministratorFormState {
  nomeCompleto: string;
  cpf: string;
  rg: string;
  email: string;
  telefone1: string;
  telefone2: string;
}

/** Estado inicial de uma linha ao adicionar (linhaAtual / linhaEditando) */
export interface ContractLineInitialState {
  negociacao: string;
  ddd: string;
  numero: string;
  tipoVenda: string;
  planId: string;
  plano: string;
  aparelho: string;
  handsetDeviceId?: string;
  aparelhoValorTotal?: number;
  aparelhoValorMensal?: number;
  valorChip: string;
  valorMensal: string;
  chipCcid: string;
  quantidade: number;
  seraUsadaPeloCliente: boolean | null;
  ehPortabilidade: boolean;
  portabilidadeNomeCompleto: string;
  portabilidadeCpf: string;
  portabilidadeOperadora: string;
  portabilidadeLinha: string;
  /** CNPJ do cedente, quando a portabilidade é entre CNPJs diferentes (cnpj-diferente). */
  portabilidadeCnpjCedente?: string;
  /** Razão social do cedente, exigida junto com o CNPJ do cedente. */
  portabilidadeRazaoSocialCedente?: string;
  /** Representantes legais do cedente (nome + CPF), exigidos em cnpj-diferente. */
  portabilidadeRepresentantes?: { nome: string; cpf: string }[];
  isEsim?: boolean;
}

/** Valores padrão para nova linha */
export const DEFAULT_CONTRACT_LINE_INITIAL: ContractLineInitialState = {
  negociacao: 'Aditivo',
  ddd: '',
  numero: '',
  tipoVenda: 'Sem aparelho',
  planId: '',
  plano: '',
  aparelho: '',
  valorChip: '7.00',
  valorMensal: '',
  chipCcid: '',
  quantidade: 1,
  seraUsadaPeloCliente: null,
  ehPortabilidade: false,
  portabilidadeNomeCompleto: '',
  portabilidadeCpf: '',
  portabilidadeOperadora: '',
  portabilidadeLinha: '',
  portabilidadeCnpjCedente: '',
  portabilidadeRazaoSocialCedente: '',
  portabilidadeRepresentantes: [],
  isEsim: false,
};

/** Linha com flags mínimas para detectar marcação de suspensão. */
export type ContractLineSuspensionSource = Pick<
  ContractLineFormItem,
  'id' | 'requerSuspensao' | 'excluirDoContrato'
>;

/** IDs das linhas já marcadas para suspensão/desativação (persistidas ou no draft). */
export function collectLinhasParaDesativarIds(
  lines: readonly ContractLineSuspensionSource[]
): Set<string> {
  const ids = lines
    .filter((l) => (l.requerSuspensao ?? false) || (l.excluirDoContrato ?? false))
    .map((l) => l.id)
    .filter((id): id is string => Boolean(id));
  return new Set(ids);
}
